import { useSelector } from 'react-redux'
import { styled, TableCell, TableRow } from '@mui/material'
import {
  DateUtils,
  muiTheme,
  type Question,
  type QuestionAnswer,
  type QuestionAnswerInput,
  QuestionContent,
  Utils,
} from '@pbt/pbt-ui-components'

import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import {
  getAnswerInputTypes,
  getFeatureToggle,
} from '~/store/reducers/constants'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  height: 40,
  border: theme.constants.tabBorder,
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  border: theme.constants.tabBorder,
  color: theme.colors.grayGray1,
  fontSize: theme.typography.body1.fontSize,
  width: '50%',
}))

export interface QuestionsListWidgetLockedItemProps {
  paddingLeft?: number
  question: Question
}

const QuestionsListWidgetLockedItem = ({
  question,
  paddingLeft = 0,
}: QuestionsListWidgetLockedItemProps) => {
  const AnswerInputTypes = useSelector(getAnswerInputTypes)
  const isCvcIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_INTAKE_QUESTIONNAIRE),
  )

  const DateSelectorAnswerInputType = Utils.findConstantIdByName(
    'Date selector',
    AnswerInputTypes,
  )

  const getSelectedInputs = () => {
    if (isCvcIntakeQuestionnaireEnabled) {
      const selectedAnswers =
        question.answers?.filter(({ selected }) => selected) || []

      return selectedAnswers.reduce(
        (inputs: QuestionAnswerInput[], current) => {
          const currentInputs = current.inputs || []
          return [...inputs, ...currentInputs]
        },
        [],
      )
    }

    const selectedAnswer =
      question.answers?.find(({ selected }) => selected) ||
      ({} as QuestionAnswer)

    return selectedAnswer.inputs || []
  }

  const getSelectedTexts = () => {
    if (isCvcIntakeQuestionnaireEnabled) {
      const selectedAnswers =
        question.answers?.filter(({ selected }) => selected) || []

      return selectedAnswers
        .reduce((texts: string[], current) => {
          const { text } = current
          return [...texts, text]
        }, [])
        .join(', ')
    }

    const selectedAnswer =
      question.answers?.find(({ selected }) => selected) ||
      ({} as QuestionAnswer)

    return selectedAnswer.text
  }

  return (
    <StyledTableRow>
      <StyledTableCell>
        <QuestionContent
          questionText={question.questionText}
          questionTextStyles={{
            fontSize: muiTheme.typography.body1.fontSize,
            paddingLeft: muiTheme.spacing(paddingLeft),
          }}
        />
      </StyledTableCell>
      <StyledTableCell>
        <Typography.Paragraph sx={{ paddingLeft }}>
          {getSelectedTexts()}
        </Typography.Paragraph>
        {getSelectedInputs().map((input) => {
          const isDate = input.inputTypeId === DateSelectorAnswerInputType

          return (
            <Typography.Paragraph key={input.id} sx={{ paddingLeft }}>
              {isDate ? DateUtils.formatDate(input.value) : input.value}
            </Typography.Paragraph>
          )
        })}
      </StyledTableCell>
    </StyledTableRow>
  )
}

export default QuestionsListWidgetLockedItem
